import { Button, Modal } from "@mantine/core";
import { IconPhoto } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ImageCropper } from "@/components/ImageCropModal";
import { ModalTitle } from "@/components/ModalTitle";
import { notify } from "@/lib/notify";

// Kaynak dosya YÜKLENMİYOR, tarayıcıda decode edilip yeniden kodlanıyor — bu yüzden tavan çıktı
// sınırından bağımsız ve cömert. Telefon fotoğrafları 3-5 MB; kullanıcıyı küçültmek için ayrı bir
// araca göndermek kırpmanın varlık sebebini ortadan kaldırırdı.
const MAX_SOURCE_SIZE = 8 * 1024 * 1024;
// R2'ye giren çıktının tavanı. API 4 MB'a kadar kabul ediyor (services/index.ts maxImageBytes) ama
// menüde basılan hiçbir kutu bunu gerektirmiyor ve görseller Worker kotasına sayılıyor — sınır
// bilerek API'nin ALTINDA. 1200×675 JPEG kalite 0.85 pratikte 150-300 KB, tavan uzak.
const MAX_UPLOAD_SIZE = 1024 * 1024;

// Görsel yükleme penceresi: TEK ekran. Görsel kadraj alanının kendisinden seçiliyor, oradan
// kadrajlanıyor, oradan yükleniyor.
//
// Önce ayrı bir yükleme penceresi + ayrı bir kadraj penceresi vardı; sonra tek pencerede iki adım
// oldu. İkisi de aynı sorundan muzdaripti: dosya seçmek ile kadrajlamak farklı ekranlar gibi
// duruyor, kullanıcı ikinci işe geçmiş hissediyordu. Şimdi düzen hiç değişmiyor — bırakma alanı
// kadrajla aynı ölçüde ve aynı yerde, seçince yerini kadraja bırakıyor.
//
// Ham dosyayı yükleyen bir yol YOK ve olmamalı. Menüdeki her görsel sabit ölçülü bir kutuya
// basılıyor (kare ürün thumb'ı, 100px kategori satırı, 128px şube kartı); kadrajı kullanıcı
// seçmezse tarayıcı kırpıyor ve konu kadrajın dışında kalıyor. İki yol bırakmak iki farklı ölçüde
// görsel demekti — marka görsellerinde de aynı sebeple "kırpmadan yükle" kaldırılmıştı.
//
// `crop`: { aspect: { width, height }, description?, type?, quality? } — hedefin ölçüsü ve biçimi,
// bkz. lib/imageTargets.
export function ImageUploadModal({
  opened,
  onClose,
  title,
  idField,
  idValue,
  uploadFn,
  invalidateKey,
  // Optional: pass both to enable removing an existing image (uploader-only modals omit them).
  currentImageUrl,
  removeFn,
  crop,
}) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!opened) {
      setFile(null);
    }
  }, [opened]);

  const mutation = useMutation({
    // Dosya durumdan değil PARAMETREDEN geliyor: yüklenecek olan seçilen dosya değil, ondan
    // üretilen kadraj.
    mutationFn: (uploaded) => {
      const formData = new FormData();
      formData.append(idField, idValue);
      formData.append("file", uploaded);
      return uploadFn(idValue, formData);
    },
    onSuccess: () => {
      notify.success("Görsel yüklendi");
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      onClose();
    },
    onError: () => notify.error("Görsel yüklenemedi"),
  });

  const removeMutation = useMutation({
    mutationFn: () => removeFn(idValue),
    onSuccess: () => {
      notify.success("Görsel kaldırıldı");
      queryClient.invalidateQueries({ queryKey: invalidateKey });
      onClose();
    },
    onError: () => notify.error("Görsel kaldırılamadı"),
  });

  const handleDrop = (files) => {
    const dropped = files[0];
    if (dropped.size > MAX_SOURCE_SIZE) {
      notify.warning("Dosya boyutu 8 MB'dan büyük olamaz");
      return;
    }
    setFile(dropped);
  };

  const handleCropped = async (cropped) => {
    if (cropped.size > MAX_UPLOAD_SIZE) {
      notify.warning("Kırpılan görsel çok büyük, kadrajı daralt");
      return;
    }
    await mutation.mutateAsync(cropped);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      size="xl"
      padding="lg"
      // Başlık iki adımda da AYNI — açıklama ("Menüde kare olarak görünür.") dropzone'da da
      // kadrajda da geçerli, adım değişince başlığın oynaması pencere değişmiş izlenimi verirdi.
      title={
        <ModalTitle
          icon={IconPhoto}
          title={title}
          description={crop.description}
        />
      }
    >
      <ImageCropper
        file={file}
        aspect={crop.aspect}
        type={crop.type}
        quality={crop.quality}
        confirming={mutation.isPending}
        onConfirm={handleCropped}
        onPickFile={handleDrop}
        maxSourceSize={MAX_SOURCE_SIZE}
        // Kaynak sınırı (MAX_SOURCE_SIZE) metinde YAZMIYOR: yüklenen dosya bu değil, ondan
        // üretilen kadraj. "max 8 MB" demek "8 MB'lık görsel kabul ediyoruz" gibi okunuyordu
        // — oysa R2'ye gidenin tavanı 1 MB. Sınır aşılırsa handleDrop sayıyı söylüyor, yani
        // rakam tam gerektiği anda geliyor.
        dropHint={
          currentImageUrl
            ? "Değiştirmek için yeni görsel bırak ya da tıkla"
            : "Görseli buraya bırak ya da tıklayarak seç"
        }
        // Dosya seçiliyken bir adım geri alır (bırakma alanına döner), seçili değilken
        // pencereyi kapatır — kullanıcı hangi durumdaysa onun çıkışı.
        onCancel={file ? () => setFile(null) : onClose}
        cancelLabel={file ? "Değiştir" : "Kapat"}
        footerStart={
          !file &&
          currentImageUrl &&
          removeFn && (
            <Button
              color="red"
              variant="light"
              loading={removeMutation.isPending}
              onClick={() => removeMutation.mutate()}
            >
              Görseli Kaldır
            </Button>
          )
        }
      />
    </Modal>
  );
}
